/**
 * Support and Feedback — CorpBrain's side of the PanLuma support widget.
 *
 * The widget is loaded in index.html with its own launcher hidden; the
 * sidebar's Support entry opens it through here. A signed-in page with no
 * sidebar (Terms, error and loading screens) gets SupportFallbackButton
 * instead: the rail entry registers itself (useSupportRailEntry) so the
 * fallback knows when it is not needed.
 *
 * INTERIM PANEL ARRANGEMENT. CorpBrain's panel is titled "Support and
 * Feedback", asks for the subject and description BEFORE the evidence,
 * labels the image picker "Attach additional images", and its thank-you after
 * Submit promises no reply ("Thanks for helping us improve the platform."). PanLuma's widget does
 * not offer any of that as a setting yet (PanLuma work item a3935aac), so
 * `arrangePanel` adjusts the panel the widget builds inside our document,
 * addressing it by the widget's element ids. The sections are MOVED, not
 * re-ordered with CSS, so keyboard and screen-reader order match the screen.
 * Delete `arrangePanel` once PanLuma ships the per-key layout; until then a
 * widget release that renames these ids is logged here and leaves its own
 * panel as it is. supportWidget.css is a different widget bug with its own
 * end — see that file.
 *
 * On Send, CorpBrain also sends its own copy to the organization's monday
 * board (supportCapture.ts) — read at the click, sent after it, never in
 * PanLuma's way.
 */

import { useLayoutEffect, useSyncExternalStore } from 'react';
import { message } from 'antd';
import i18n from '@/i18n';
import type { User } from '@/types/models';
import { supportWidgetConfig, type SupportReportFields } from './supportReport';
import { armReportCopy } from './supportCapture';
import './supportWidget.css';

declare global {
  interface Window {
    PanLumaWidget?: { open: () => void | Promise<void> };
    PanLumaWidgetConfig?: { hideButton: boolean } & Partial<SupportReportFields>;
  }
}

/** Evidence sections, in the order they go under Priority. */
const EVIDENCE_IDS = [
  'panluma-console-section',
  'panluma-screenshot-section',
  'panluma-attach-section',
] as const;

function arrangePanel(): void {
  const panel = document.getElementById('panluma-widget-panel');
  const form = document.getElementById('panluma-widget-form');
  const title = document.querySelector<HTMLElement>('#panluma-widget-header h2');
  const attachLabel = document.querySelector<HTMLElement>('label[for="panluma-attach-input"]');
  // Under Priority: the product picker (hidden when the host names the
  // product, as CorpBrain does) and Submit follow it.
  const anchor = document.getElementById('panluma-product-field')
    ?? document.getElementById('panluma-widget-submit');
  const sections = EVIDENCE_IDS.map(id => document.getElementById(id));

  if (!panel || !form || !title || !attachLabel || !anchor || sections.some(s => !s)) {
    console.warn('[support] PanLuma panel markup changed; left as the widget built it');
    return;
  }
  // The widget builds its panel once and reuses it on every later open.
  if (panel.dataset.corpbrainArranged) return;

  // The widget picks its language once, when it first builds the panel —
  // match that, not whatever the app has switched to since. So after a
  // language switch the panel stays in the old language until a reload.
  const t = i18n.getFixedT(panel.getAttribute('dir') === 'rtl' ? 'he' : 'en');
  title.textContent = t('sidebar.supportAndFeedback');
  attachLabel.textContent = t('sidebar.supportAttachImages');
  // The thank-you after Submit: no "we'll get back to you" — nobody promises
  // a reply to feedback. Built hidden with the panel, shown on success.
  const thanks = document.querySelector<HTMLElement>('#panluma-widget-success p');
  if (thanks) thanks.textContent = t('sidebar.supportThanks');
  else console.warn('[support] PanLuma success markup changed; its own thank-you is shown');

  for (const section of sections) {
    form.insertBefore(section as HTMLElement, anchor);
  }
  panel.dataset.corpbrainArranged = 'true';
}

/** Open the panel for `user` (see supportReport.ts for what it is told). */
export function openSupportPanel(user: User | null): void {
  if (window.PanLumaWidgetConfig) {
    Object.assign(window.PanLumaWidgetConfig, supportWidgetConfig(user));
  }
  if (!window.PanLumaWidget) {
    // The widget script loads async from app.panluma.ai — blocked, failed,
    // or not arrived yet. Say so: a Support button that does nothing reads
    // as one more thing broken, to someone who came here to report one.
    console.warn('[support] PanLuma widget is not loaded; cannot open Support');
    message.warning(i18n.t('sidebar.supportUnavailable'));
    return;
  }
  // open() builds the panel synchronously, before its first await, so the
  // markup exists as soon as it returns.
  const opening = window.PanLumaWidget.open();
  arrangePanel();
  armReportCopy(user);
  Promise.resolve(opening).catch(err => console.error('[support] PanLuma widget failed to open', err));
}

// ── Is a sidebar Support entry on screen? ────────────────────────────────
// Layout effects, so a page change (old rail unmounts, new one mounts in the
// same commit) never paints a frame with the fallback showing.
let railEntries = 0;
const railListeners = new Set<() => void>();
const notifyRail = () => railListeners.forEach(fn => fn());
const subscribeRail = (fn: () => void) => {
  railListeners.add(fn);
  return () => { railListeners.delete(fn); };
};
const hasRailEntry = () => railEntries > 0;

/** Called by the sidebar's Support entry while it is mounted. */
export function useSupportRailEntry(): void {
  useLayoutEffect(() => {
    railEntries += 1;
    notifyRail();
    return () => {
      railEntries -= 1;
      notifyRail();
    };
  }, []);
}

export function useHasSupportRailEntry(): boolean {
  return useSyncExternalStore(subscribeRail, hasRailEntry);
}
